/**
 * PE Papers Archiv — Entwürfe & fertige Dokumente in pepapers_documents.
 * status: 'entwurf' = später fortsetzen | 'final' = fertig archiviert
 */

import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDocs,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import useAdminStore from '../store/adminStore';

export type PePapersArchiveStatus = 'entwurf' | 'final';

export type PePapersDocumentType =
    | 'arbeitsvertrag'
    | 'abmahnung-ermahnung'
    | 'arbeitszeugnis'
    | 'kuendigung-aufhebungsvertrag'
    | 'unwiderrufliche-freistellung'
    | 'vertragsanpassung-kombi'
    | 'urlaubskuerzung-elternzeit'
    | 'fortbildungsrueckzahlung'
    | 'modell-release'
    | 'mitfuehrpflicht-zeiterfassung'
    | 'dienstanweisung-social-media'
    | 'dienstanweisung-social'
    | 'dienstanweisung-equipment'
    | 'dienstanweisung-kombi'
    | 'bgw-gefaehrdungsbeurteilung'
    | 'bgw-uvv-jahresunterweisung'
    | 'consultation-quote'
    | 'dsgvo-verfahrensdokumentation'
    | 'gobd-verfahrensdokumentation'
    | 'pe-invoice'
    | 'sachbezug-50';

export type PePapersArchiveDoc = {
    id: string;
    userId: string;
    documentType: PePapersDocumentType | string;
    status: PePapersArchiveStatus;
    salonName?: string;
    mitarbeiterName?: string;
    createdAt?: Date | null;
    updatedAt?: Date | null;
    confirmedDisclaimer?: boolean;
    currentStep?: number;
    [key: string]: unknown;
};

/** documentType → Modal-id auf PePapersPage */
export const PE_PAPERS_DOC_TO_MODAL: Record<string, string> = {
    arbeitsvertrag: 'arbeitsvertrag',
    'abmahnung-ermahnung': 'abmahnung',
    arbeitszeugnis: 'arbeitszeugnis',
    'kuendigung-aufhebungsvertrag': 'kuendigung',
    'unwiderrufliche-freistellung': 'unwiderrufliche-freistellung',
    'vertragsanpassung-kombi': 'vertragsanpassung',
    'urlaubskuerzung-elternzeit': 'urlaubskuerzung',
    fortbildungsrueckzahlung: 'fortbildungsrueckzahlung',
    'modell-release': 'modell-release',
    'mitfuehrpflicht-zeiterfassung': 'mitfuehrpflicht-zeiterfassung',
    'dienstanweisung-social-media': 'dienstanweisung-social-media',
    'dienstanweisung-social': 'dienstanweisung-social-media',
    'dienstanweisung-equipment': 'dienstanweisung-social-media',
    'dienstanweisung-kombi': 'dienstanweisung-social-media',
    'bgw-gefaehrdungsbeurteilung': 'bgw',
    'bgw-uvv-jahresunterweisung': 'bgw-uvv-unterweisung',
    'consultation-quote': 'consultation-quote',
    'dsgvo-verfahrensdokumentation': 'dsgvo',
    'gobd-verfahrensdokumentation': 'gobd',
    'pe-invoice': 'pe-invoice',
    'sachbezug-50': 'sachbezug-50',
};

export const PE_PAPERS_DOC_LABELS: Record<string, string> = {
    arbeitsvertrag: 'Arbeitsvertrag',
    'abmahnung-ermahnung': 'Abmahnung / Ermahnung',
    arbeitszeugnis: 'Arbeitszeugnis',
    'kuendigung-aufhebungsvertrag': 'Kündigung / Aufhebungsvertrag',
    'unwiderrufliche-freistellung': 'Unwiderrufliche Freistellung',
    'vertragsanpassung-kombi': 'Änderungsvertrag',
    'urlaubskuerzung-elternzeit': 'Urlaubskürzung Elternzeit',
    fortbildungsrueckzahlung: 'Fortbildungsrückzahlung',
    'modell-release': 'Modell-Release',
    'mitfuehrpflicht-zeiterfassung': 'Mitführpflicht & Zeiterfassung',
    'dienstanweisung-social-media': 'Dienstanweisung Social Media & Equipment',
    'dienstanweisung-social': 'Dienstanweisung Social Media & Equipment',
    'dienstanweisung-equipment': 'Dienstanweisung Social Media & Equipment',
    'dienstanweisung-kombi': 'Dienstanweisung Social Media & Equipment',
    'bgw-gefaehrdungsbeurteilung': 'BGW-Gefährdungsbeurteilung',
    'bgw-uvv-jahresunterweisung': 'BGW- & UVV-Jahresunterweisung',
    'consultation-quote': 'Beratungsprotokoll & Kostenvoranschlag',
    'dsgvo-verfahrensdokumentation': 'DSGVO-Verfahrensdokumentation',
    'gobd-verfahrensdokumentation': 'GoBD-Verfahrensdokumentation',
    'pe-invoice': 'Rechnung / Kostenvoranschlag',
    'sachbezug-50': '50-€-Sachbezugsvereinbarung',
};

export const resolvePePapersOwnerUid = (): string => {
    try {
        const god = useAdminStore.getState().impersonatedClientId;
        if (god) return god;
    } catch (_) { /* ignore */ }
    return auth.currentUser?.uid || '';
};

const toDate = (v: unknown): Date | null => {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof (v as any)?.toDate === 'function') {
        try { return (v as any).toDate(); } catch { return null; }
    }
    if (typeof v === 'string' || typeof v === 'number') {
        const d = new Date(v);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
};

export const listPePapersArchive = async (uid?: string): Promise<PePapersArchiveDoc[]> => {
    const userId = uid || resolvePePapersOwnerUid();
    if (!userId) return [];

    const q = query(
        collection(db, 'pepapers_documents'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
    );

    try {
        const snap = await getDocs(q);
        return snap.docs.map((d) => {
            const data = d.data() || {};
            const status: PePapersArchiveStatus =
                data.status === 'entwurf' ? 'entwurf' : 'final';
            return {
                ...data,
                id: d.id,
                userId: String(data.userId || userId),
                documentType: String(data.documentType || ''),
                status,
                createdAt: toDate(data.createdAt),
                updatedAt: toDate(data.updatedAt) || toDate(data.createdAt),
            } as PePapersArchiveDoc;
        });
    } catch (err) {
        // Fallback ohne orderBy (fehlender Index)
        console.warn('listPePapersArchive orderBy fehlgeschlagen, Fallback:', err);
        const snap = await getDocs(
            query(collection(db, 'pepapers_documents'), where('userId', '==', userId))
        );
        return snap.docs
            .map((d) => {
                const data = d.data() || {};
                const status: PePapersArchiveStatus =
                    data.status === 'entwurf' ? 'entwurf' : 'final';
                return {
                    ...data,
                    id: d.id,
                    userId: String(data.userId || userId),
                    documentType: String(data.documentType || ''),
                    status,
                    createdAt: toDate(data.createdAt),
                    updatedAt: toDate(data.updatedAt) || toDate(data.createdAt),
                } as PePapersArchiveDoc;
            })
            .sort((a, b) => (b.updatedAt?.getTime() || 0) - (a.updatedAt?.getTime() || 0));
    }
};

export type SavePePapersArchiveInput = {
    /** Bestehende Doc-ID → update statt create */
    id?: string | null;
    documentType: PePapersDocumentType | string;
    status: PePapersArchiveStatus;
    salonName?: string;
    mitarbeiterName?: string;
    confirmedDisclaimer?: boolean;
    currentStep?: number;
    payload: Record<string, unknown>;
};

export const savePePapersArchiveDoc = async (
    input: SavePePapersArchiveInput
): Promise<string> => {
    const userId = resolvePePapersOwnerUid();
    if (!userId) throw new Error('Nicht angemeldet');

    const { id, documentType, status, salonName, mitarbeiterName, confirmedDisclaimer, currentStep, payload } =
        input;

    // Firestore akzeptiert kein undefined — Payload hart bereinigen.
    const cleanPayload =
        payload && typeof payload === 'object'
            ? (JSON.parse(JSON.stringify(payload)) as Record<string, unknown>)
            : {};

    const base = {
        userId,
        documentType,
        status,
        salonName: salonName || '',
        mitarbeiterName: mitarbeiterName || '',
        confirmedDisclaimer: !!confirmedDisclaimer,
        currentStep: typeof currentStep === 'number' ? currentStep : null,
        updatedAt: serverTimestamp(),
        ...cleanPayload,
    };

    if (id) {
        await updateDoc(doc(db, 'pepapers_documents', id), base);
        return id;
    }

    const ref = await addDoc(collection(db, 'pepapers_documents'), {
        ...base,
        createdAt: serverTimestamp(),
    });
    return ref.id;
};

export const deletePePapersArchiveDoc = async (id: string): Promise<void> => {
    if (!id) return;
    await deleteDoc(doc(db, 'pepapers_documents', id));
};

export const formatPePapersArchiveDate = (d?: Date | null): string => {
    if (!d) return '—';
    try {
        return d.toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '—';
    }
};
